import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import {
  Lock,
  Eye,
  EyeOff,
  Save,
  Settings,
  FileText,
  Palette,
  Image,
  MessageSquare,
  Plus,
  Trash2,
  Edit3,
  LogOut,
  CheckCircle2,
  Home,
  Users,
  Layers,
  BookOpen,
  Mail,
} from 'lucide-react';
import { toast } from 'sonner';
import { adminCredentials } from '../../data/mock';

// Helper to get data from localStorage or use defaults
const getStoredData = (key, defaultValue) => {
  try {
    const stored = localStorage.getItem(`kc_admin_${key}`);
    return stored ? JSON.parse(stored) : defaultValue;
  } catch {
    return defaultValue;
  }
};

// Helper to save data to localStorage
const saveData = (key, value) => {
  localStorage.setItem(`kc_admin_${key}`, JSON.stringify(value));
};

// Login Component
const AdminLogin = ({ onLogin }) => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API}/api/admin/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username, password }),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        sessionStorage.setItem('kc_admin_logged', 'true');
        sessionStorage.setItem('kc_admin_token', data.token || 'local');
        onLogin();
      } else {
        setError(data.message || 'Credenciais inválidas');
      }
    } catch (err) {
      // Fallback to local auth if backend is unavailable
      const storedCredentials = getStoredData('credentials', { 
        username: 'admin', 
        password: adminCredentials.password 
      });
      
      if (username === storedCredentials.username && password === storedCredentials.password) {
        sessionStorage.setItem('kc_admin_logged', 'true');
        sessionStorage.setItem('kc_admin_token', 'local');
        onLogin();
      } else {
        setError('Usuário ou senha incorretos');
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-[#0a0f14] px-4">
      <Card className="w-full max-w-md bg-[#111920] border-[#1e293b]">
        <CardHeader className="text-center">
          <div className="w-16 h-16 rounded-2xl bg-[#329780] flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-white" />
          </div>
          <CardTitle className="text-2xl text-white">Admin KC Byte iT</CardTitle>
          <p className="text-slate-400 text-sm mt-2">
            Área restrita. Insira suas credenciais para continuar.
          </p>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Usuário</label>
              <Input
                type="text"
                value={username}
                onChange={(e) => {
                  setUsername(e.target.value);
                  setError('');
                }}
                placeholder="Digite seu usuário"
                className="bg-[#0a0f14] border-[#1e293b] text-white h-12"
                required
              />
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium text-slate-300">Senha</label>
              <div className="relative">
                <Input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => {
                    setPassword(e.target.value);
                    setError('');
                  }}
                  placeholder="Digite sua senha"
                  className="bg-[#0a0f14] border-[#1e293b] text-white pr-10 h-12"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>
            {error && <p className="text-red-400 text-sm">{error}</p>}
            <Button
              type="submit"
              disabled={isLoading}
              className="w-full bg-[#329780] hover:bg-[#287a68] text-white h-12"
            >
              {isLoading ? 'Entrando...' : 'Entrar'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
};

// Admin Dashboard Component
const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('home');
  const [isSaving, setIsSaving] = useState(false);

  // Site Config State
  const [siteConfig, setSiteConfig] = useState(
    getStoredData('siteConfig', {
      companyName: 'KC Byte iT',
      email: 'contato@kcbyteit.com.br',
      phone: '(11) 99999-9999',
      whatsapp: '5511999999999',
      whatsappMessage: 'Olá! Gostaria de saber mais sobre os serviços da KC Byte iT.',
      address: 'Atendimento remoto em todo Brasil',
      workingHours: 'Segunda a Sexta, 8h às 18h',
      linkedin: 'https://linkedin.com/company/kcbyteit',
      instagram: 'https://instagram.com/kcbyteit',
      facebook: 'https://facebook.com/kcbyteit',
      twitter: 'https://twitter.com/kcbyteit',
    })
  );

  // Theme Config State
  const [themeConfig, setThemeConfig] = useState(
    getStoredData('themeConfig', {
      primaryColor: '#329780',
      backgroundColor: '#0a0f14',
      textColor: '#f8fafc',
      accentColor: '#4ab39a',
    })
  );

  // Hero Content State
  const [heroContent, setHeroContent] = useState(
    getStoredData('heroContent', {
      title: 'TI que resolve. Segurança que protege. Automação que escala.',
      subtitle: 'Soluções completas em tecnologia da informação para empresas que buscam eficiência, segurança e crescimento sustentável.',
      ctaPrimary: 'Solicitar Orçamento',
      ctaSecondary: 'Falar no WhatsApp',
    })
  );

  // Testimonials State
  const [testimonials, setTestimonials] = useState(
    getStoredData('testimonials', [
      {
        id: 1,
        name: 'Carlos Eduardo Silva',
        role: 'Diretor de TI',
        company: 'Indústria Metalúrgica São Paulo',
        content: 'A KC Byte iT transformou nossa gestão de TI. O gerenciamento do GLPI e as automações implementadas reduziram nosso tempo de resposta em 60%.',
      },
      {
        id: 2,
        name: 'Ana Paula Rodrigues',
        role: 'CEO',
        company: 'Startup Tech Solutions',
        content: 'Contratamos para análise de vulnerabilidade e ficamos impressionados com a profundidade do relatório. Recomendo fortemente.',
      },
      {
        id: 3,
        name: 'Roberto Mendes',
        role: 'Gerente de Operações',
        company: 'Logística Express Brasil',
        content: 'As integrações com APIs desenvolvidas pela equipe conectaram todos os nossos sistemas. Economia de 40% em custos operacionais.',
      },
    ])
  );

  // Partners State
  const [partners, setPartners] = useState(
    getStoredData('partners', [
      { id: 1, name: 'Google Cloud' },
      { id: 2, name: 'Cloudflare' },
      { id: 3, name: 'CrowdStrike' },
      { id: 4, name: 'GLPI' },
      { id: 5, name: 'n8n' },
    ])
  );

  // Admin Credentials State
  const [adminUsername, setAdminUsername] = useState(
    getStoredData('credentials', { username: 'admin', password: adminCredentials.password }).username
  );
  const [adminPassword, setAdminPassword] = useState(
    getStoredData('credentials', { username: 'admin', password: adminCredentials.password }).password
  );

  const handleSave = async () => {
    setIsSaving(true);
    
    // Save all data to localStorage
    saveData('siteConfig', siteConfig);
    saveData('themeConfig', themeConfig);
    saveData('heroContent', heroContent);
    saveData('testimonials', testimonials);
    saveData('partners', partners);
    saveData('credentials', { username: adminUsername, password: adminPassword });

    // Try to save to backend as well
    try {
      const API = process.env.REACT_APP_BACKEND_URL;
      await fetch(`${API}/api/admin/settings`, {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${sessionStorage.getItem('kc_admin_token')}`
        },
        body: JSON.stringify({
          siteConfig,
          themeConfig,
          heroContent,
          testimonials,
          partners,
        }),
      });
    } catch (err) {
      console.log('Backend unavailable, saved to localStorage only');
    }

    await new Promise((resolve) => setTimeout(resolve, 500));
    setIsSaving(false);
    toast.success('Alterações salvas com sucesso!');
  };

  const addTestimonial = () => {
    const newId = Math.max(...testimonials.map((t) => t.id), 0) + 1;
    setTestimonials([
      ...testimonials,
      {
        id: newId,
        name: 'Novo Cliente',
        role: 'Cargo',
        company: 'Empresa',
        content: 'Depoimento...',
      },
    ]);
  };

  const removeTestimonial = (id) => {
    setTestimonials(testimonials.filter((t) => t.id !== id));
  };

  const updateTestimonial = (id, field, value) => {
    setTestimonials(
      testimonials.map((t) => (t.id === id ? { ...t, [field]: value } : t))
    );
  };

  const addPartner = () => {
    const newId = Math.max(...partners.map((p) => p.id), 0) + 1;
    setPartners([...partners, { id: newId, name: 'Novo Parceiro' }]);
  };

  const removePartner = (id) => {
    setPartners(partners.filter((p) => p.id !== id));
  };

  const updatePartner = (id, name) => {
    setPartners(partners.map((p) => (p.id === id ? { ...p, name } : p)));
  };

  return (
    <div className="min-h-screen bg-[#0a0f14]">
      {/* Header */}
      <header className="bg-[#111920] border-b border-[#1e293b] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 rounded-lg bg-[#329780] flex items-center justify-center">
              <Settings className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-lg font-bold text-white">Painel Admin</h1>
              <p className="text-xs text-slate-400">KC Byte iT</p>
            </div>
          </div>
          <div className="flex items-center space-x-4">
            <Button
              onClick={handleSave}
              disabled={isSaving}
              className="bg-[#329780] hover:bg-[#287a68] text-white"
            >
              {isSaving ? (
                'Salvando...'
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Salvar Alterações
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={onLogout}
              className="border-slate-700 text-slate-300 hover:bg-red-500/10 hover:border-red-500/50 hover:text-red-400"
            >
              <LogOut className="w-4 h-4 mr-2" />
              Sair
            </Button>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-7xl mx-auto px-4 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="bg-[#111920] border border-[#1e293b] p-1 mb-8">
            <TabsTrigger value="home" className="data-[state=active]:bg-[#329780] data-[state=active]:text-white">
              <Home className="w-4 h-4 mr-2" />
              Home
            </TabsTrigger>
            <TabsTrigger value="config" className="data-[state=active]:bg-[#329780] data-[state=active]:text-white">
              <Settings className="w-4 h-4 mr-2" />
              Configurações
            </TabsTrigger>
            <TabsTrigger value="theme" className="data-[state=active]:bg-[#329780] data-[state=active]:text-white">
              <Palette className="w-4 h-4 mr-2" />
              Tema
            </TabsTrigger>
            <TabsTrigger value="testimonials" className="data-[state=active]:bg-[#329780] data-[state=active]:text-white">
              <MessageSquare className="w-4 h-4 mr-2" />
              Depoimentos
            </TabsTrigger>
            <TabsTrigger value="partners" className="data-[state=active]:bg-[#329780] data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Parceiros
            </TabsTrigger>
            <TabsTrigger value="security" className="data-[state=active]:bg-[#329780] data-[state=active]:text-white">
              <Lock className="w-4 h-4 mr-2" />
              Segurança
            </TabsTrigger>
          </TabsList>

          {/* Home Content Tab */}
          <TabsContent value="home">
            <Card className="bg-[#111920] border-[#1e293b]">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <FileText className="w-5 h-5 mr-2 text-[#329780]" />
                  Conteúdo da Home
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Título do Hero</label>
                  <Textarea
                    value={heroContent.title}
                    onChange={(e) => setHeroContent({ ...heroContent, title: e.target.value })}
                    className="bg-[#0a0f14] border-[#1e293b] text-white"
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Subtítulo</label>
                  <Textarea
                    value={heroContent.subtitle}
                    onChange={(e) => setHeroContent({ ...heroContent, subtitle: e.target.value })}
                    className="bg-[#0a0f14] border-[#1e293b] text-white"
                    rows={3}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Botão Primário</label>
                    <Input
                      value={heroContent.ctaPrimary}
                      onChange={(e) => setHeroContent({ ...heroContent, ctaPrimary: e.target.value })}
                      className="bg-[#0a0f14] border-[#1e293b] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Botão Secundário</label>
                    <Input
                      value={heroContent.ctaSecondary}
                      onChange={(e) => setHeroContent({ ...heroContent, ctaSecondary: e.target.value })}
                      className="bg-[#0a0f14] border-[#1e293b] text-white"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Config Tab */}
          <TabsContent value="config">
            <Card className="bg-[#111920] border-[#1e293b]">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Settings className="w-5 h-5 mr-2 text-[#329780]" />
                  Configurações do Site
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Nome da Empresa</label>
                    <Input
                      value={siteConfig.companyName}
                      onChange={(e) => setSiteConfig({ ...siteConfig, companyName: e.target.value })}
                      className="bg-[#0a0f14] border-[#1e293b] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">E-mail</label>
                    <Input
                      value={siteConfig.email}
                      onChange={(e) => setSiteConfig({ ...siteConfig, email: e.target.value })}
                      className="bg-[#0a0f14] border-[#1e293b] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Telefone</label>
                    <Input
                      value={siteConfig.phone}
                      onChange={(e) => setSiteConfig({ ...siteConfig, phone: e.target.value })}
                      className="bg-[#0a0f14] border-[#1e293b] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Número WhatsApp (com código)</label>
                    <Input
                      value={siteConfig.whatsapp}
                      onChange={(e) => setSiteConfig({ ...siteConfig, whatsapp: e.target.value })}
                      className="bg-[#0a0f14] border-[#1e293b] text-white"
                      placeholder="5511999999999"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <label className="text-sm font-medium text-slate-300">Mensagem Padrão WhatsApp</label>
                  <Textarea
                    value={siteConfig.whatsappMessage}
                    onChange={(e) => setSiteConfig({ ...siteConfig, whatsappMessage: e.target.value })}
                    className="bg-[#0a0f14] border-[#1e293b] text-white"
                    rows={2}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Endereço</label>
                    <Input
                      value={siteConfig.address}
                      onChange={(e) => setSiteConfig({ ...siteConfig, address: e.target.value })}
                      className="bg-[#0a0f14] border-[#1e293b] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Horário de Atendimento</label>
                    <Input
                      value={siteConfig.workingHours}
                      onChange={(e) => setSiteConfig({ ...siteConfig, workingHours: e.target.value })}
                      className="bg-[#0a0f14] border-[#1e293b] text-white"
                    />
                  </div>
                </div>
                <div className="border-t border-[#1e293b] pt-6">
                  <h3 className="text-lg font-medium text-white mb-4">Redes Sociais</h3>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">LinkedIn</label>
                      <Input
                        value={siteConfig.linkedin}
                        onChange={(e) => setSiteConfig({ ...siteConfig, linkedin: e.target.value })}
                        className="bg-[#0a0f14] border-[#1e293b] text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Instagram</label>
                      <Input
                        value={siteConfig.instagram}
                        onChange={(e) => setSiteConfig({ ...siteConfig, instagram: e.target.value })}
                        className="bg-[#0a0f14] border-[#1e293b] text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Facebook</label>
                      <Input
                        value={siteConfig.facebook}
                        onChange={(e) => setSiteConfig({ ...siteConfig, facebook: e.target.value })}
                        className="bg-[#0a0f14] border-[#1e293b] text-white"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-sm font-medium text-slate-300">Twitter/X</label>
                      <Input
                        value={siteConfig.twitter}
                        onChange={(e) => setSiteConfig({ ...siteConfig, twitter: e.target.value })}
                        className="bg-[#0a0f14] border-[#1e293b] text-white"
                      />
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Theme Tab */}
          <TabsContent value="theme">
            <Card className="bg-[#111920] border-[#1e293b]">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Palette className="w-5 h-5 mr-2 text-[#329780]" />
                  Personalização do Tema
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Cor Primária</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={themeConfig.primaryColor}
                        onChange={(e) => setThemeConfig({ ...themeConfig, primaryColor: e.target.value })}
                        className="w-12 h-12 rounded cursor-pointer border-0"
                      />
                      <Input
                        value={themeConfig.primaryColor}
                        onChange={(e) => setThemeConfig({ ...themeConfig, primaryColor: e.target.value })}
                        className="bg-[#0a0f14] border-[#1e293b] text-white flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Cor de Destaque</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={themeConfig.accentColor}
                        onChange={(e) => setThemeConfig({ ...themeConfig, accentColor: e.target.value })}
                        className="w-12 h-12 rounded cursor-pointer border-0"
                      />
                      <Input
                        value={themeConfig.accentColor}
                        onChange={(e) => setThemeConfig({ ...themeConfig, accentColor: e.target.value })}
                        className="bg-[#0a0f14] border-[#1e293b] text-white flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Cor de Fundo</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={themeConfig.backgroundColor}
                        onChange={(e) => setThemeConfig({ ...themeConfig, backgroundColor: e.target.value })}
                        className="w-12 h-12 rounded cursor-pointer border-0"
                      />
                      <Input
                        value={themeConfig.backgroundColor}
                        onChange={(e) => setThemeConfig({ ...themeConfig, backgroundColor: e.target.value })}
                        className="bg-[#0a0f14] border-[#1e293b] text-white flex-1"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Cor do Texto</label>
                    <div className="flex items-center space-x-3">
                      <input
                        type="color"
                        value={themeConfig.textColor}
                        onChange={(e) => setThemeConfig({ ...themeConfig, textColor: e.target.value })}
                        className="w-12 h-12 rounded cursor-pointer border-0"
                      />
                      <Input
                        value={themeConfig.textColor}
                        onChange={(e) => setThemeConfig({ ...themeConfig, textColor: e.target.value })}
                        className="bg-[#0a0f14] border-[#1e293b] text-white flex-1"
                      />
                    </div>
                  </div>
                </div>

                {/* Preview */}
                <div className="border-t border-[#1e293b] pt-6">
                  <h3 className="text-lg font-medium text-white mb-4">Preview</h3>
                  <div
                    className="rounded-xl p-8 border"
                    style={{ backgroundColor: themeConfig.backgroundColor, borderColor: themeConfig.primaryColor }}
                  >
                    <div
                      className="w-full h-12 rounded-lg mb-4 flex items-center justify-center"
                      style={{ backgroundColor: themeConfig.primaryColor }}
                    >
                      <span style={{ color: themeConfig.textColor }} className="font-semibold">
                        Botão Primário
                      </span>
                    </div>
                    <p style={{ color: themeConfig.textColor }} className="mb-2">
                      Texto principal com a cor selecionada.
                    </p>
                    <p style={{ color: themeConfig.accentColor }}>
                      Texto com cor de destaque.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Testimonials Tab */}
          <TabsContent value="testimonials">
            <Card className="bg-[#111920] border-[#1e293b]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center">
                  <MessageSquare className="w-5 h-5 mr-2 text-[#329780]" />
                  Depoimentos
                </CardTitle>
                <Button onClick={addTestimonial} size="sm" className="bg-[#329780] hover:bg-[#287a68]">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </CardHeader>
              <CardContent className="space-y-6">
                {testimonials.map((testimonial, index) => (
                  <div key={testimonial.id} className="p-4 bg-[#0a0f14] rounded-lg border border-[#1e293b] space-y-4">
                    <div className="flex items-center justify-between">
                      <span className="text-slate-400 text-sm">Depoimento #{index + 1}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => removeTestimonial(testimonial.id)}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                    <div className="grid md:grid-cols-3 gap-4">
                      <Input
                        value={testimonial.name}
                        onChange={(e) => updateTestimonial(testimonial.id, 'name', e.target.value)}
                        placeholder="Nome"
                        className="bg-[#111920] border-[#1e293b] text-white"
                      />
                      <Input
                        value={testimonial.role}
                        onChange={(e) => updateTestimonial(testimonial.id, 'role', e.target.value)}
                        placeholder="Cargo"
                        className="bg-[#111920] border-[#1e293b] text-white"
                      />
                      <Input
                        value={testimonial.company}
                        onChange={(e) => updateTestimonial(testimonial.id, 'company', e.target.value)}
                        placeholder="Empresa"
                        className="bg-[#111920] border-[#1e293b] text-white"
                      />
                    </div>
                    <Textarea
                      value={testimonial.content}
                      onChange={(e) => updateTestimonial(testimonial.id, 'content', e.target.value)}
                      placeholder="Conteúdo do depoimento"
                      className="bg-[#111920] border-[#1e293b] text-white"
                      rows={3}
                    />
                  </div>
                ))}
              </CardContent>
            </Card>
          </TabsContent>

          {/* Partners Tab */}
          <TabsContent value="partners">
            <Card className="bg-[#111920] border-[#1e293b]">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-white flex items-center">
                  <Users className="w-5 h-5 mr-2 text-[#329780]" />
                  Parceiros / Tecnologias
                </CardTitle>
                <Button onClick={addPartner} size="sm" className="bg-[#329780] hover:bg-[#287a68]">
                  <Plus className="w-4 h-4 mr-2" />
                  Adicionar
                </Button>
              </CardHeader>
              <CardContent>
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {partners.map((partner) => (
                    <div
                      key={partner.id}
                      className="flex items-center space-x-3 p-3 bg-[#0a0f14] rounded-lg border border-[#1e293b]"
                    >
                      <Input
                        value={partner.name}
                        onChange={(e) => updatePartner(partner.id, e.target.value)}
                        className="bg-[#111920] border-[#1e293b] text-white flex-1"
                      />
                      <Button
                        variant="outline"
                        size="icon"
                        onClick={() => removePartner(partner.id)}
                        className="border-red-500/30 text-red-400 hover:bg-red-500/10 flex-shrink-0"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Security Tab */}
          <TabsContent value="security">
            <Card className="bg-[#111920] border-[#1e293b]">
              <CardHeader>
                <CardTitle className="text-white flex items-center">
                  <Lock className="w-5 h-5 mr-2 text-[#329780]" />
                  Segurança
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid md:grid-cols-2 gap-6 max-w-2xl">
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Usuário de Acesso</label>
                    <Input
                      type="text"
                      value={adminUsername}
                      onChange={(e) => setAdminUsername(e.target.value)}
                      className="bg-[#0a0f14] border-[#1e293b] text-white"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-sm font-medium text-slate-300">Senha de Acesso</label>
                    <Input
                      type="password"
                      value={adminPassword}
                      onChange={(e) => setAdminPassword(e.target.value)}
                      className="bg-[#0a0f14] border-[#1e293b] text-white"
                    />
                  </div>
                </div>
                <p className="text-xs text-slate-500 max-w-2xl">
                  Altere as credenciais de acesso ao painel administrativo. Lembre-se de salvar após alterar.
                </p>

                <div className="border-t border-[#1e293b] pt-6">
                  <h3 className="text-lg font-medium text-white mb-4">Informações de Acesso</h3>
                  <div className="bg-[#0a0f14] rounded-lg p-4 space-y-2">
                    <p className="text-slate-400 text-sm">
                      <span className="text-slate-300 font-medium">URL do Admin:</span>{' '}
                      <code className="text-[#329780]">/kc-admin-9f3a1</code>
                    </p>
                    <p className="text-slate-400 text-sm">
                      Esta URL não é indexada pelos mecanismos de busca e não aparece em nenhum link do site.
                    </p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

// Main Admin Page Component
export const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(
    sessionStorage.getItem('kc_admin_logged') === 'true'
  );
  const navigate = useNavigate();

  const handleLogin = () => {
    setIsLoggedIn(true);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('kc_admin_logged');
    setIsLoggedIn(false);
  };

  if (!isLoggedIn) {
    return <AdminLogin onLogin={handleLogin} />;
  }

  return <AdminDashboard onLogout={handleLogout} />;
};
